import React, { useEffect, useState } from "react";
import { Application, getApplication } from "@/api/applications";
import {
  Privilege,
  getPrivilegesByApplicationId,
} from "@/api/privileges";
import AddButton from "@/ui-controls/AddButton";
import AppPrivilegeDetailList from "@/components/privileges/appPrivileges/AppPrivilegeDetailList";
import NewAppPrivilegePanel from "@/components/privileges/appPrivileges/NewAppPrivilegePanel";
import SearchBox from "@/ui-controls/SearchBox";
import { IconButton, MessageBar, MessageBarType } from "@fluentui/react";
import { useNavigate } from "react-router-dom";
interface Props {
  ApplicationId: number;
  ShowOnlyNewAppPrivilegePanel: boolean;
  onCloseNewAppPrivilegePanel?: () => any;
  onAddNewPrivilege?: (privilege: Privilege) => any;
  onApplicationLoaded?: (name: string) => void;
  breadcrumbTitle?: string;
  breadcrumbSubtitle?: React.ReactNode;
}

const AppPrivileges = (props: Props) => {
  const navigate = useNavigate();
  const [privileges, setPrivileges] = useState<Privilege[]>([]);
  const [application, setApplication] = useState<Application | undefined>(
    undefined
  );
  const [loadingPrivileges, setLoadingPrivileges] = useState<boolean>(false);
  const [loadingApplication, setLoadingApplication] = useState<boolean>(false);
  const [newAppPrivilegePanelOpenned, setNewAppPrivilegePanelOpenned] =
    useState<boolean>(props.ShowOnlyNewAppPrivilegePanel);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [filteredPrivileges, setFilteredPrivileges] = useState<Privilege[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");

  useEffect(() => {
    if (props.ApplicationId) {
      getAppPrivileges();
      getApplicationById();
    }
  }, [props.ApplicationId]);

  const getAppPrivileges = () => {
    setLoadingPrivileges(true);
    getPrivilegesByApplicationId(props.ApplicationId)
      .then((result) => {
        setPrivileges(result);
        setFilteredPrivileges(result);
        setLoadingPrivileges(false);
      })
      .catch((error) => {
        handleError(error);
        setLoadingPrivileges(false);
      });
  };

  const getApplicationById = () => {
    setLoadingApplication(true);
    getApplication(props.ApplicationId)
      .then((result) => {
        setApplication(result);
        if (props.onApplicationLoaded && result?.Name) {
          props.onApplicationLoaded(result.Name);
        }
        setLoadingApplication(false);
      })
      .catch((error) => {
        handleError(error);
        setLoadingApplication(false);
      });
  };

  const handleError = (error: any) => {
    if (typeof error === "string") {
      setErrorMessage(error);
      return;
    }
    if (typeof error?.response?.data === "string") {
      setErrorMessage(error.response.data);
      return;
    }
    const messageFromResponse =
      error?.response?.data?.Message ||
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.response?.data?.title;
    if (messageFromResponse) {
      setErrorMessage(messageFromResponse);
      return;
    }
    if (error?.response?.data) {
      setErrorMessage(JSON.stringify(error.response.data));
      return;
    }
    setErrorMessage(error?.message || "An unexpected error occurred.");
  };

  const handlePrivilegeSearch = (searchValue: string) => {
    setSearchValue(searchValue);

    const filteredPrivileges = privileges.filter(
      (privilege) =>
        privilege.Name.toLowerCase().includes(searchValue.toLowerCase()) ||
        (privilege.Description &&
          privilege.Description.toLowerCase().includes(
            searchValue.toLowerCase()
          ))
    );

    setFilteredPrivileges(filteredPrivileges);
  };

  const handleRefresh = () => {
    setSearchValue("");
    setFilteredPrivileges([]);
    getAppPrivileges();
  };

  return (
    <>
      {!props.ShowOnlyNewAppPrivilegePanel ? (
        <div className="page-header">
          <div className="breadcrumb-left-section">
            <IconButton
              iconProps={{ iconName: "Back" }}
              title="Back to Applications"
              onClick={() => navigate("/applications")}
              styles={{
                root: { width: 40, height: 40 },
                rootHovered: { backgroundColor: "#f3f2f1" },
                icon: { fontSize: 18, color: "#0078d4" },
                rootPressed: { backgroundColor: "#e1dfdd" },
              }}
            />
            <div>
              {props.breadcrumbTitle && (
                <h2 className="page-breadcrumb-title">
                  {props.breadcrumbTitle}
                </h2>
              )}
              {props.breadcrumbSubtitle && (
                <div style={{ color: "#605e5c", fontSize: "14px" }}>
                  {props.breadcrumbSubtitle}
                </div>
              )}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <AddButton
              text="Privilege"
              onClick={() => setNewAppPrivilegePanelOpenned(true)}
            />
            <SearchBox placeholder="Search" onSearch={handlePrivilegeSearch} />
            <IconButton
              iconProps={{ iconName: "Refresh" }}
              title="Refresh"
              onClick={handleRefresh}
              styles={{
                root: { width: 40, height: 40 },
                rootHovered: { backgroundColor: "#f3f2f1" },
                icon: { fontSize: 18, color: "#0078d4" },
                rootPressed: { backgroundColor: "#e1dfdd" },
              }}
            />
          </div>
        </div>
      ) : null}
      {!props.ShowOnlyNewAppPrivilegePanel ? (
        <AppPrivilegeDetailList
          PrivilegeList={searchValue === "" ? privileges : filteredPrivileges}
          onDelete={(appPrivilege) => {
            setPrivileges((prev) =>
              prev.filter((r) => r.Id !== appPrivilege.Id)
            );
          }}
        />
      ) : null}
      <NewAppPrivilegePanel
        application={application}
        //appPrivileges={privileges}
        isPanelOpenned={newAppPrivilegePanelOpenned}
        onPanelClosed={() => {
          setNewAppPrivilegePanelOpenned(false);
          if (props.onCloseNewAppPrivilegePanel)
            props.onCloseNewAppPrivilegePanel();
        }}
        onSaveNewPrivilege={(newPrivilege) => {
          setPrivileges((prev) => [...prev, newPrivilege]);
          if (props.onAddNewPrivilege) props.onAddNewPrivilege(newPrivilege);
        }}
        onError={(error) => handleError(error)}
      />
      {errorMessage && (
        <MessageBar
          messageBarType={MessageBarType.error}
          isMultiline={true}
          onDismiss={() => setErrorMessage(null)}
          styles={{
            root: {
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              zIndex: 1001,
            },
          }}
        >
          {errorMessage}
        </MessageBar>
      )}
    </>
  );
};

export default AppPrivileges;
